import {
  AI_QUALITY_CHECKS,
  CompanyService,
  TemplatesService,
  isAiQualityCheck,
  qualityFindingKey,
  resolveQualitySettings,
  runDeterministicQualityChecks,
  supersedeQualityFindings,
  type TemplateQualityCheck,
  type TemplateQualityFinding,
  type TemplateQualityResolution,
  type TemplateQualityResolutionKind,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useTemplateQualityStream } from '@/composables/useTemplateQualityStream';
import { useFeatureFlagStore } from './featureFlagStore';
import { useTemplateStore } from './templateStore';

// Deterministic findings are computed in-browser from the loaded template (so
// badges stay live without a round trip); AI findings are streamed in and
// cached. Both are filtered by the same effective settings + ignore list.
//
// On a tenant without the AI feature flag the deterministic half still works
// on its own, so the panel stays — only the AI checks are switched off, here
// rather than per component, so no consumer can leak them.

// Module-scoped so `reset()`/unmount can cancel it. Inside `runAiCheck` the
// AbortController would be trapped in that call's closure.
const qualityStream = useTemplateQualityStream();

export type TemplateQualityStoreState = {
  templateId: string | null;
  effectiveSettings: Record<string, boolean>;
  companyDefaults: Record<string, boolean>;
  templateOverrides: Record<string, boolean>;
  ignores: TemplateQualityResolution[];
  aiFindings: TemplateQualityFinding[];
  isLoading: boolean;
  isRunningAiCheck: boolean;
  aiProgress: { processed: number; total: number } | null;
  aiError: string | null;
  // Per-session run selection: runtime-only, never persisted. `null` = not
  // configured yet, so the saved defaults apply.
  sessionChecks: Record<string, boolean> | null;
};

export const useTemplateQualityStore = defineStore('templateQualityStore', {
  state: (): TemplateQualityStoreState => ({
    templateId: null,
    effectiveSettings: {},
    companyDefaults: {},
    templateOverrides: {},
    ignores: [],
    aiFindings: [],
    isLoading: false,
    isRunningAiCheck: false,
    aiProgress: null,
    aiError: null,
    sessionChecks: null,
  }),

  getters: {
    /** Whether the tenant has AI at all (`VITE_<tenant>_AI`). */
    hasAiChecks(): boolean {
      return useFeatureFlagStore().hasAI;
    },

    // Gates the findings: per-run selection if configured, else saved defaults.
    // Everything that filters by check uses this, not `effectiveSettings`.
    // Without the AI feature flag every AI check is forced off, which also
    // hides any stored AI finding left over from before the flag was cleared.
    activeSettings(state): Record<string, boolean> {
      const settings = state.sessionChecks ?? state.effectiveSettings;
      if (this.hasAiChecks) {
        return settings;
      }

      return {
        ...settings,
        ...Object.fromEntries(AI_QUALITY_CHECKS.map((check) => [check, false])),
      };
    },

    // Permanently ignored ("never flag this again").
    ignoredKeys(state): Set<string> {
      return new Set(
        state.ignores
          .filter((ignore) => ignore.kind !== 'resolved')
          .map((ignore) => qualityFindingKey(ignore)),
      );
    },

    // Marked solved. Temporary: reconciled on every load, so a resolve that
    // didn't actually fix anything comes back.
    resolvedKeys(state): Set<string> {
      return new Set(
        state.ignores
          .filter((ignore) => ignore.kind === 'resolved')
          .map((ignore) => qualityFindingKey(ignore)),
      );
    },

    hiddenKeys(): Set<string> {
      return new Set([...this.ignoredKeys, ...this.resolvedKeys]);
    },

    // The template only when set here AND loaded in templateStore, else null.
    // Shared guard for every getter deriving from the live template.
    loadedTemplate(state) {
      if (!state.templateId) {
        return null;
      }
      const templateStore = useTemplateStore();
      if (!templateStore.hasTemplate(state.templateId)) {
        return null;
      }

      return templateStore.template(state.templateId);
    },

    // Every deterministic finding, with NO disposition filtering — what is
    // actually wrong right now. Reconciliation needs this: "is this ignored
    // problem still there?" can't be asked of a list that excludes ignores.
    rawDeterministicFindings(): TemplateQualityFinding[] {
      const template = this.loadedTemplate;
      if (!template) {
        return [];
      }

      return runDeterministicQualityChecks(template, this.activeSettings, null);
    },

    unresolvedDeterministicFindings(): TemplateQualityFinding[] {
      const ignored = this.ignoredKeys;

      return this.rawDeterministicFindings.filter(
        (finding: TemplateQualityFinding) =>
          !ignored.has(qualityFindingKey(finding)),
      );
    },

    deterministicFindings(): TemplateQualityFinding[] {
      const resolved = this.resolvedKeys;

      return this.unresolvedDeterministicFindings.filter(
        (finding: TemplateQualityFinding) =>
          !resolved.has(qualityFindingKey(finding)),
      );
    },

    // Current title of every checkable entity, by id. Stored AI findings carry
    // no `entity_title`, so it is resolved here against the live template.
    entityTitles(): Map<string, string> {
      const template = this.loadedTemplate;

      return template ? collectEntityTitles(template) : new Map();
    },

    // Per-entity display number + sort key, derived from the TEMPLATE rather
    // than from the findings. Findings are emitted per check family, so an
    // entity used to rank by whichever of its findings happened to come first
    // — disposing of that one re-ranked the entity mid-list.
    entityIndex(): Map<string, QualityEntityIndexEntry> {
      const template = this.loadedTemplate;

      return template ? collectEntityIndex(template) : new Map();
    },

    /** Lexicographic compare of two entities' sort keys; unknown ids sort last. */
    compareEntities() {
      return (leftId: string, rightId: string): number => {
        const left = this.entityIndex.get(leftId)?.key;
        const right = this.entityIndex.get(rightId)?.key;
        if (!left || !right) {
          return left ? -1 : right ? 1 : 0;
        }
        for (let i = 0; i < Math.max(left.length, right.length); i++) {
          // A shorter key sorts first, so a missing segment counts as -1.
          const diff = (left[i] ?? -1) - (right[i] ?? -1);
          if (diff !== 0) {
            return diff;
          }
        }

        return 0;
      };
    },

    /** The number shown on the map, or null for entities that have none. */
    entityNumberOf() {
      return (entityId: string): number | null =>
        this.entityIndex.get(entityId)?.number ?? null;
    },

    // AI findings come from storage, so the template may have moved on —
    // filter by settings, dispositions, staleness and entity existence.
    visibleAiFindings(state): TemplateQualityFinding[] {
      const hidden = this.hiddenKeys;
      const titles = this.entityTitles;

      // Existence is only judged against a loaded template: an empty titles
      // map may just mean it isn't in the store yet, and blanking the panel on
      // that guess is worse than showing a stale row.
      const isTemplateLoaded = this.loadedTemplate !== null;

      return state.aiFindings
        .filter(
          (finding) =>
            this.activeSettings[finding.check] !== false &&
            !hidden.has(qualityFindingKey(finding)) &&
            !this.isStaleFinding(finding) &&
            (!isTemplateLoaded || titles.has(finding.entity_id)),
        )
        .map((finding) => ({
          ...finding,
          entity_title:
            titles.get(finding.entity_id) ?? finding.entity_title ?? '',
        }));
    },

    // True when a stored finding is about text the user has since edited: its
    // `message`/`proposal` describe `judged_text`, so a changed value makes
    // them misleading. Live-streamed findings carry no `judged_text`.
    isStaleFinding() {
      return (finding: TemplateQualityFinding): boolean => {
        const judged = finding.judged_text;
        if (judged === undefined || judged === null || !finding.field) {
          return false;
        }

        const current = this.currentFieldValue(finding);

        // Unknown entity — leave it visible rather than hide on a guess.
        return current === undefined ? false : (current ?? '') !== judged;
      };
    },

    /** Current value of the field a finding is about, or undefined if unknown. */
    currentFieldValue() {
      return (finding: TemplateQualityFinding): string | null | undefined => {
        const template = this.loadedTemplate;
        if (!template || !finding.field) {
          return undefined;
        }

        if (finding.entity_type === 'activity') {
          const activity = template.activities.find(
            (item) => item.id === finding.entity_id,
          );

          return activity
            ? ((activity as Record<string, unknown>)[finding.field] as string)
            : undefined;
        }

        if (finding.entity_type === 'completion_criterion') {
          for (const activity of template.activities) {
            const criterion = activity.completion_criteria?.find(
              (item) => item.id === finding.entity_id,
            );
            if (criterion) {
              return (criterion as Record<string, unknown>)[
                finding.field
              ] as string;
            }
          }

          return undefined;
        }

        if (finding.entity_type === 'milestone') {
          for (const phase of template.phases) {
            const milestone = milestonesOf(phase).find(
              (item) => item.id === finding.entity_id,
            );
            if (milestone) {
              return (milestone as Record<string, unknown>)[
                finding.field
              ] as string;
            }
          }
        }

        return undefined;
      };
    },

    allFindings(): TemplateQualityFinding[] {
      // Not a plain concat — an `ai_suggest_function_*` finding supersedes the
      // plain `unassigned_function_*` one for the same entity.
      return supersedeQualityFindings(
        this.deterministicFindings,
        this.visibleAiFindings,
      );
    },

    // The activity owning a completion-criterion finding. Prefers
    // `context.activity_id` but must keep the template-scan fallback: stored AI
    // findings come back with `context: null`.
    parentActivityIdOf() {
      return (finding: TemplateQualityFinding): string | null => {
        const fromContext = finding.context?.activity_id;
        if (typeof fromContext === 'string') {
          return fromContext;
        }
        const template = this.loadedTemplate;
        if (!template) {
          return null;
        }
        const activity = template.activities.find((act) =>
          (act.completion_criteria ?? []).some(
            (criterion) => criterion.id === finding.entity_id,
          ),
        );

        return activity?.id ?? null;
      };
    },

    // Dispositions that no longer describe reality — what
    // `reconcileDispositions` deletes. A getter so callers can watch it: an
    // edit retires a stale ignore immediately, not on the next `load()`.
    staleDispositions(state): TemplateQualityResolution[] {
      if (!state.templateId) {
        return [];
      }
      const stillBroken = new Set(
        this.rawDeterministicFindings.map((finding: TemplateQualityFinding) =>
          qualityFindingKey(finding),
        ),
      );
      const storedByKey = new Map(
        state.aiFindings.map((finding) => [
          qualityFindingKey(finding),
          finding,
        ]),
      );

      return state.ignores.filter((row) => {
        if (isAiQualityCheck(row.check)) {
          // "Still broken?" is unanswerable client-side, but "edited since the
          // model judged it?" is — via the stored finding's `judged_text`. No
          // stored finding means nothing to compare, so leave the ignore.
          const stored = storedByKey.get(qualityFindingKey(row));

          return stored ? this.isStaleFinding(stored) : false;
        }
        const isBroken = stillBroken.has(qualityFindingKey(row));

        return row.kind === 'resolved' ? isBroken : !isBroken;
      });
    },

    findingsFor() {
      return (entityId: string): TemplateQualityFinding[] =>
        this.allFindings.filter((finding) => finding.entity_id === entityId);
    },

    // An entity's findings plus those of the completion criteria it owns. A
    // criterion has no canvas node, so the owning activity claims its findings.
    // Identical to `findingsFor` for non-activity entities.
    findingsForWithCriteria() {
      return (entityId: string): TemplateQualityFinding[] =>
        this.allFindings.filter(
          (finding) =>
            finding.entity_id === entityId ||
            (finding.entity_type === 'completion_criterion' &&
              this.parentActivityIdOf(finding) === entityId),
        );
    },

    hasFindings() {
      return (entityId: string): boolean =>
        this.allFindings.some((finding) => finding.entity_id === entityId);
    },

    // Ignored findings as displayable rows for the "show ignored" list; an
    // ignore whose entity no longer exists is dropped. Only permanent ignores
    // belong here — a "solved" mark reconciles itself.
    ignoredFindings(
      state,
    ): Array<
      TemplateQualityResolution & { title: string; number: number | null }
    > {
      const titles = this.entityTitles;
      const hasAi = this.hasAiChecks;

      return (
        state.ignores
          .flatMap((ignore) => {
            if (ignore.kind === 'resolved') {
              return [];
            }
            // An ignore left from before the AI flag was cleared: its finding can
            // no longer be shown, so neither should the row that suppresses it.
            if (!hasAi && isAiQualityCheck(ignore.check)) {
              return [];
            }
            const title = titles.get(ignore.entity_id);

            return title === undefined
              ? []
              : [
                  {
                    ...ignore,
                    title,
                    number: this.entityNumberOf(ignore.entity_id),
                  },
                ];
          })
          // Same order as the findings list, so restoring one doesn't send the
          // user hunting for it in a differently sorted list.
          .sort((left, right) =>
            this.compareEntities(left.entity_id, right.entity_id),
          )
      );
    },
  },

  actions: {
    // Loads settings + ignores. Deterministic findings need no fetch.
    async load(templateId: string, force = false) {
      // Guarding on `templateId` rather than `isLoading` so a concurrent second
      // call can't fire duplicate requests. Refresh via `force`.
      if (!force && this.templateId === templateId) {
        return;
      }
      if (this.templateId !== templateId) {
        // So switching templates never shows the previous one's findings.
        this.aiFindings = [];
        this.aiProgress = null;
        this.aiError = null;
      }
      this.templateId = templateId;
      this.isLoading = true;
      try {
        const [settings, ignores, storedFindings] = await Promise.all([
          TemplatesService.getTemplateQualitySettings(templateId),
          TemplatesService.listTemplateQualityResolutions(templateId),
          // Persisted, so opening the panel shows the last run rather than
          // re-billing the model. Regeneration is explicit.
          TemplatesService.listTemplateQualityFindings(templateId),
        ]);
        // Bail if the user switched templates while this fetch was in flight.
        if (this.templateId !== templateId) {
          return;
        }
        this.effectiveSettings = settings.effective;
        this.companyDefaults = settings.company_defaults;
        this.templateOverrides = settings.template_overrides;
        this.ignores = ignores;
        this.aiFindings = storedFindings;

        // Best-effort: a failure here must not break loading the panel.
        try {
          await this.reconcileDispositions();
        } catch {
          // Left in place; re-checked on next load.
        }
      } finally {
        if (this.templateId === templateId) {
          this.isLoading = false;
        }
      }
    },

    // Replaces the cached AI findings wholesale (stream `done`); incremental
    // events use `addAiFinding`.
    setAiFindings(findings: TemplateQualityFinding[]) {
      this.aiFindings = findings;
    },

    // Runs the AI half, appending findings as they stream then replacing them
    // with the terminal set. One run at a time.
    async runAiCheck(checks?: TemplateQualityCheck[]) {
      const templateId = this.templateId;
      // The UI hides the trigger without the flag; this is the backstop, since
      // a run on an AI-less tenant fails in the model client with a 500.
      if (!templateId || this.isRunningAiCheck || !this.hasAiChecks) {
        return;
      }
      // Restrict the run to the session's selected checks, so the dialog
      // governs what is sent to the model, not just what is shown afterwards.
      const requested =
        checks ??
        (this.sessionChecks
          ? (Object.keys(this.sessionChecks).filter(
              (check) => this.sessionChecks?.[check],
            ) as TemplateQualityCheck[])
          : undefined);
      const { startStream } = qualityStream;
      this.isRunningAiCheck = true;
      this.aiError = null;
      this.aiProgress = null;
      this.aiFindings = [];
      try {
        await startStream(templateId, requested ? { checks: requested } : {}, {
          onStatus: (status) => {
            this.aiProgress = status;
          },
          onFinding: (finding) => {
            // Guard against a stale stream after a mid-run template switch.
            if (this.templateId === templateId) {
              this.addAiFinding(finding);
            }
          },
          onDone: (data) => {
            if (this.templateId === templateId) {
              this.setAiFindings(data.findings);
            }
          },
          // The stream reports every failure here before rethrowing.
          onError: (message) => {
            this.aiError = message;
          },
        });
      } finally {
        this.isRunningAiCheck = false;
        this.aiProgress = null;
      }
    },

    // Appends a streamed finding, deduped by key against replayed events.
    addAiFinding(finding: TemplateQualityFinding) {
      const key = qualityFindingKey(finding);
      if (this.aiFindings.some((f) => qualityFindingKey(f) === key)) {
        return;
      }
      this.aiFindings.push(finding);
    },

    // Dispose of a finding. `ignored` (default) is permanent; `resolved` is
    // temporary and reconciled away if the problem is still there.
    async ignore(
      finding: TemplateQualityFinding,
      kind: TemplateQualityResolutionKind = 'ignored',
    ) {
      if (!this.templateId) {
        return;
      }
      const ignore = await TemplatesService.createTemplateQualityResolution(
        this.templateId,
        {
          check: finding.check,
          entity_type: finding.entity_type,
          entity_id: finding.entity_id,
          kind,
        },
      );
      // Natural key is (entity, check): a different kind switches the row
      // rather than adding one, so replace in place.
      const key = qualityFindingKey(ignore);
      const existing = this.ignores.findIndex(
        (row) => qualityFindingKey(row) === key,
      );
      if (existing >= 0) {
        this.ignores.splice(existing, 1, ignore);
      } else {
        this.ignores.push(ignore);
      }

      // The stored AI finding is kept even for a permanent ignore: it is the
      // only record of `judged_text`, which `reconcileDispositions` needs to
      // retire the ignore once the entity is edited. It stays hidden anyway.
    },

    // Drop a stored finding once actioned so it doesn't reappear on next open.
    // Best-effort: a live-streamed finding has no id and isn't stored.
    async discardFinding(finding: TemplateQualityFinding) {
      const id = finding.id;
      this.aiFindings = this.aiFindings.filter(
        (item) => qualityFindingKey(item) !== qualityFindingKey(finding),
      );
      if (!this.templateId || !id) {
        return;
      }
      try {
        await TemplatesService.deleteTemplateQualityFinding(
          this.templateId,
          id,
        );
      } catch {
        // Replaced by the next run anyway; a cleanup failure must not break
        // applying the proposal.
      }
    },

    // Record the per-run check selection. Runtime-only, cleared on reset.
    setSessionChecks(selection: Record<string, boolean>) {
      this.sessionChecks = { ...selection };
    },

    /** "I fixed this manually" — a temporary resolve. */
    async markSolved(finding: TemplateQualityFinding) {
      await this.ignore(finding, 'resolved');
    },

    // Drop dispositions that no longer describe reality (runs on load): a
    // `resolved` whose problem is still detected, or an `ignored` whose
    // problem is gone. AI checks can't be re-evaluated client-side, so an AI
    // disposition is retired only when its `judged_text` no longer matches.
    async reconcileDispositions() {
      const stale = this.staleDispositions;
      if (!this.templateId || stale.length === 0) {
        return;
      }

      await Promise.all(
        stale.map((row) =>
          TemplatesService.deleteTemplateQualityResolution(
            this.templateId as string,
            row.id,
          ),
        ),
      );
      const staleIds = new Set(stale.map((row) => row.id));
      this.ignores = this.ignores.filter((row) => !staleIds.has(row.id));

      // The stored finding behind a retired AI ignore is now dead weight — it
      // can't be shown (stale) or reused by the verdict cache — so drop it.
      const staleAiKeys = new Set(
        stale
          .filter((row) => isAiQualityCheck(row.check))
          .map((row) => qualityFindingKey(row)),
      );
      const staleFindings = this.aiFindings.filter((finding) =>
        staleAiKeys.has(qualityFindingKey(finding)),
      );
      await Promise.all(
        staleFindings.map((finding) => this.discardFinding(finding)),
      );
    },

    async unignore(ignoreId: string) {
      if (!this.templateId) {
        return;
      }
      await TemplatesService.deleteTemplateQualityResolution(
        this.templateId,
        ignoreId,
      );
      this.ignores = this.ignores.filter((ignore) => ignore.id !== ignoreId);
    },

    // `null` clears the per-template override so the company default applies.
    async updateSettings(overrides: Record<string, boolean | null>) {
      if (!this.templateId) {
        return;
      }
      const settings = await TemplatesService.updateTemplateQualitySettings(
        this.templateId,
        { template_overrides: overrides },
      );
      this.effectiveSettings = settings.effective;
      this.companyDefaults = settings.company_defaults;
      this.templateOverrides = settings.template_overrides;
    },

    // Realm-wide defaults, kept here so effective settings recompute locally
    // after a change without refetching every open template.
    async loadCompanyDefaults() {
      const settings = await CompanyService.getCompanyQualitySettings();
      this.companyDefaults = settings.company_defaults;

      return settings.company_defaults;
    },

    async updateCompanyDefaults(defaults: Record<string, boolean | null>) {
      const settings = await CompanyService.updateCompanyQualitySettings({
        company_defaults: defaults,
      });
      this.companyDefaults = settings.company_defaults;
      this.effectiveSettings = resolveQualitySettings(
        this.companyDefaults,
        this.templateOverrides,
      );

      return settings.company_defaults;
    },

    reset() {
      // Abort any in-flight run, else the server keeps judging (and billing).
      qualityStream.cancelStream();
      this.templateId = null;
      // Per-session, so the dialog asks again next time.
      this.sessionChecks = null;
      this.effectiveSettings = {};
      this.companyDefaults = {};
      this.templateOverrides = {};
      this.ignores = [];
      this.aiFindings = [];
      this.isLoading = false;
      this.isRunningAiCheck = false;
      this.aiProgress = null;
      this.aiError = null;
    },
  },
});

function milestonesOf<T extends { id: string }>(phase: {
  intermediate_milestones?: T[] | null;
  final_milestone?: T | null;
}): T[] {
  return [
    ...(phase.intermediate_milestones ?? []),
    ...(phase.final_milestone ? [phase.final_milestone] : []),
  ];
}

/**
 * `number` is what the map paints on the entity (`sequence`); `key` is the
 * lexicographic sort key. Entities without a number of their own borrow their
 * parent's so they stay with it: a criterion sorts by
 * `[activity.sequence, positionInActivity]`.
 */
type QualityEntityIndexEntry = { number: number | null; key: number[] };

// Unnumbered entities must still sort after every numbered one when a sequence
// is missing, rather than jumping to the top on a `?? 0`.
const NO_SEQUENCE = Number.MAX_SAFE_INTEGER;

function collectEntityIndex(template: {
  activities: Array<{
    id: string;
    sequence?: number | null;
    completion_criteria?: Array<{ id: string }> | null;
  }>;
  phases: Array<{
    id: string;
    intermediate_milestones?: Array<{
      id: string;
      sequence?: number | null;
    }> | null;
    final_milestone?: { id: string; sequence?: number | null } | null;
  }>;
  documents: Array<{ id: string }>;
}): Map<string, QualityEntityIndexEntry> {
  const index = new Map<string, QualityEntityIndexEntry>();

  for (const activity of template.activities) {
    const sequence = activity.sequence ?? NO_SEQUENCE;
    index.set(activity.id, {
      number: activity.sequence ?? null,
      key: [sequence],
    });
    (activity.completion_criteria ?? []).forEach((criterion, position) => {
      index.set(criterion.id, { number: null, key: [sequence, position] });
    });
  }

  // Phases and documents carry no number, so they keep template order.
  template.phases.forEach((phase, position) => {
    index.set(phase.id, { number: null, key: [position] });
    for (const milestone of milestonesOf(phase)) {
      index.set(milestone.id, {
        number: milestone.sequence ?? null,
        key: [milestone.sequence ?? NO_SEQUENCE],
      });
    }
  });

  template.documents.forEach((document, position) => {
    index.set(document.id, { number: null, key: [position] });
  });

  return index;
}

function collectEntityTitles(template: {
  activities: Array<{
    id: string;
    title: string;
    completion_criteria?: Array<{ id: string; title: string }> | null;
  }>;
  phases: Array<{
    id: string;
    title: string;
    intermediate_milestones?: Array<{ id: string; title: string }> | null;
    final_milestone?: { id: string; title: string } | null;
  }>;
  documents: Array<{ id: string; title: string }>;
}): Map<string, string> {
  const titles = new Map<string, string>();
  for (const activity of template.activities) {
    titles.set(activity.id, activity.title);
    for (const criterion of activity.completion_criteria ?? []) {
      titles.set(criterion.id, criterion.title);
    }
  }
  for (const phase of template.phases) {
    titles.set(phase.id, phase.title);
    for (const milestone of milestonesOf(phase)) {
      titles.set(milestone.id, milestone.title);
    }
  }
  for (const document of template.documents) {
    titles.set(document.id, document.title);
  }

  return titles;
}

export type { TemplateQualityCheck, TemplateQualityFinding };
